import * as React from "react";
import { DataTable } from "../data-table";
import { formatDateTime } from "../../format";
import { Button } from "../../button";
import { IntentBadge } from "../status-badge";
import { Icon } from "../../icon";
import { useApi } from "../../api/client";
import { changelog as changelogContract } from "../../api/contracts/database/changelog";

/**
 * Write/DDL audit changelog, newest-first. Every mutating statement (executed
 * OR refused) appears here. "Export as SQL" downloads the successful
 * statements in order — the migration-drift escape hatch (PRD §5.5, §13).
 */
export function ChangelogTable() {
  const { data, isLoading } = useApi(changelogContract.list, { query: {} });
  const [expanded, setExpanded] = React.useState<number | null>(null);
  const rows = data ?? [];

  return (
      <DataTable
        title="Change audit"
        count={rows.length}
        strongHeader
        headerAction={
          <Button asChild variant="outline" size="sm">
            <a href={changelogContract.exportSql.path()} download>
              <Icon name="download" size="sm" /> Export as SQL
            </a>
          </Button>
        }
        rows={rows}
        rowKey={(r) => r.id}
        initialSort={{ key: "when", dir: "desc" }}
        empty={isLoading ? "Loading…" : undefined}
        emptyState={
          isLoading
            ? undefined
            : {
                icon: <Icon name="history" />,
                title: "No changes logged yet",
                description:
                  "Every write or DDL statement run in the explorer — executed or refused — is recorded here. Run one from the Table or SQL editor to see it.",
              }
        }
        columns={[
          {
            key: "when",
            header: "When",
            width: "w-44",
            cellClassName: "text-muted-foreground tabular-nums",
            sort: (a, b) => a.created_at.localeCompare(b.created_at),
            defaultDesc: true,
            render: (r) => formatDateTime(r.created_at),
          },
          {
            key: "source",
            header: "Source",
            width: "w-28",
            cellClassName: "text-muted-foreground",
            sort: (a, b) => a.source.localeCompare(b.source),
            render: (r) => r.source,
          },
          {
            key: "kind",
            header: "Kind",
            width: "w-20",
            cellClassName: "text-muted-foreground",
            sort: (a, b) => a.kind.localeCompare(b.kind),
            render: (r) => r.kind,
          },
          {
            key: "ok",
            header: "OK",
            width: "w-16",
            sort: (a, b) => Number(a.ok) - Number(b.ok),
            render: (r) =>
              r.ok ? (
                <IntentBadge intent="success">ok</IntentBadge>
              ) : (
                <IntentBadge intent="danger">refused</IntentBadge>
              ),
          },
          {
            key: "changes",
            header: "Changes",
            width: "w-20",
            cellClassName: "tabular-nums text-muted-foreground",
            sort: (a, b) => (a.changes ?? -1) - (b.changes ?? -1),
            render: (r) => r.changes ?? "—",
          },
          {
            key: "sql",
            header: "SQL",
            render: (r) => (
              <>
                <button
                  onClick={() => setExpanded(expanded === r.id ? null : r.id)}
                  className="block max-w-md truncate text-left font-mono type-subtext text-foreground hover:text-muted-foreground"
                  title="Click to expand"
                >
                  {expanded === r.id
                    ? r.sql
                    : r.sql.replace(/\s+/g, " ").slice(0, 80)}
                </button>
                {r.error ? (
                  <span className="block type-subtext text-destructive">
                    {r.error}
                  </span>
                ) : null}
              </>
            ),
          },
          {
            key: "by",
            header: "By",
            width: "w-40",
            cellClassName: "type-subtext text-muted-foreground",
            sort: (a, b) =>
              (a.agent_id ?? String(a.actor_id ?? "")).localeCompare(
                b.agent_id ?? String(b.actor_id ?? ""),
              ),
            render: (r) => (
              <>
                {r.agent_id ??
                  (r.actor_id != null ? `actor ${r.actor_id}` : "—")}
                {r.model ? <span className="block">{r.model}</span> : null}
              </>
            ),
          },
        ]}
      />
  );
}
